import { createFileRoute, redirect } from "@tanstack/react-router";
import type { RouterContextWithSession } from "../../../router";
import { useState, useCallback, useMemo } from "react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Bot, Users, UserCheck, BarChart3, ShieldCheck, 
  Sparkles, Briefcase, Scale, DollarSign, BrainCircuit, Landmark,
  BookOpen, Terminal, Network
} from "lucide-react";
import { MarketplacePage } from "../../../features/agents/pages/MarketplacePage";
import { TeamDirectory, type TeamMember } from "../../../features/agents/components/TeamDirectory";
import { ApprovalInbox, type PendingApproval } from "../../../features/agents/components/ApprovalInbox";
import { AgentDashboard, type AgentRunLog } from "../../../features/agents/pages/AgentDashboard";
import { SkillStudio } from "../../../features/agents/components/SkillStudio";
import { SandboxManager } from "../../../features/agents/components/SandboxManager";
import { AgentSwarmRoom } from "../../../features/agents/components/AgentSwarmRoom";
import { type AgentTemplate, type AgentInstance } from "../../../features/agents/components/AgentCard";
import { AgentConfigModal } from "../../../features/agents/components/AgentConfigModal";
import { useTierAccess } from "@/hooks/useTierAccess";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import {
  useMarketplaceTemplates,
  useOrgMarketplaceAgentInstances,
  useDeployMarketplaceAgent,
  useUpdateMarketplaceAgentInstance,
} from "../../../features/agents/hooks/useAgents";
import { usePendingApprovals, useProcessApproval } from "../../../features/agents/hooks/usePendingApprovals";
import { useAgentRuns } from "../../../features/agents/hooks/useAgentRuns";
import { toast } from "@/components/ui/sonner";
import type { MarketplaceAgentTemplate, MarketplaceAgentInstance, MarketplaceAgentInstanceConfig } from "@flowstate/types";

export const Route = createFileRoute("/_admin/cortex/marketplace")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  pendingComponent: () => <PageSkeleton rows={8} variant="cards" />,
  errorComponent: createRouteError("Agent Marketplace"),
  component: AgentMarketplaceContainer,
});

// ── Template rendering helpers ───────────────────────────────────────────

const TIER_COLOR: Record<MarketplaceAgentTemplate["tier"], string> = {
  personal: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  team: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  self_hosted: "bg-violet-500/10 text-violet-500 border-violet-500/20",
};

function templateIcon(slug: string) {
  if (slug.includes("crm")) return <Briefcase className="h-5 w-5" />;
  if (slug.includes("sales")) return <DollarSign className="h-5 w-5" />;
  if (slug.includes("legal")) return <Scale className="h-5 w-5" />;
  if (slug.includes("research")) return <BrainCircuit className="h-5 w-5" />;
  if (slug.includes("accounting")) return <Landmark className="h-5 w-5" />;
  return <Bot className="h-5 w-5" />;
}

function mapToAgentTemplate(t: MarketplaceAgentTemplate): AgentTemplate {
  const details = t.required_tools.map((tool) => `${tool}`);
  return {
    ...t,
    icon: templateIcon(t.slug),
    color: TIER_COLOR[t.tier],
    details: details.length ? details : ["No capabilities listed"],
  };
}

function mapToAgentInstance(i: MarketplaceAgentInstance): AgentInstance {
  return i;
}

function mapToTeamMember(
  instance: MarketplaceAgentInstance,
  template: MarketplaceAgentTemplate | undefined
): TeamMember {
  return {
    id: instance.principal_id,
    name: `${template?.name ?? "Agent"} (Agent)`,
    email: `${template?.slug ?? "agent"}@mycelium.internal`,
    role: template?.description.split(".")[0] ?? "AI Specialist",
    type: "agent",
    status: instance.status,
    agentTemplateSlug: template?.slug,
  };
}

function AgentMarketplaceContainer() {
  const { user } = useAuth();
  const { currentOrganization } = useCurrentOrganization();
  const { tier, hasFeature } = useTierAccess();
  const marketplaceEnabled = hasFeature("marketplace_agents");

  // Real data hooks
  const { data: templates = [], isLoading: templatesLoading } = useMarketplaceTemplates();
  const { data: instances = [], isLoading: instancesLoading } = useOrgMarketplaceAgentInstances();
  const { data: approvals = [], isLoading: approvalsLoading } = usePendingApprovals();
  const { data: logs = [], isLoading: logsLoading } = useAgentRuns(50);

  const deploy = useDeployMarketplaceAgent();
  const updateInstance = useUpdateMarketplaceAgentInstance();
  const processApproval = useProcessApproval();

  // Configuration Modal States
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<AgentInstance | null>(null);

  const agentTemplates = useMemo(() => templates.map(mapToAgentTemplate), [templates]);
  const agentInstances = useMemo(() => instances.map(mapToAgentInstance), [instances]);

  const teamMembers = useMemo<TeamMember[]>(() => {
    const humans: TeamMember[] = user
      ? [
          {
            id: user.id,
            name: `${user.email ?? "Owner"} (You)`,
            email: user.email ?? "",
            role: "System Operator",
            type: "human",
            status: "active",
          },
        ]
      : [];

    const agents = instances.map((inst) => {
      const template = templates.find((t) => t.id === inst.template_id);
      return mapToTeamMember(inst, template);
    });

    return [...humans, ...agents];
  }, [instances, templates, user]);

  // Open configuration modal
  const handleOpenConfig = useCallback((instance: AgentInstance, template: AgentTemplate) => {
    setSelectedInstance(instance);
    setSelectedTemplate(template);
    setConfigModalOpen(true);
  }, []);

  // Deploy an agent from the marketplace
  const handleActivateAgent = useCallback(
    async (templateId: string) => {
      if (!marketplaceEnabled) {
        toast.error("Marketplace locked", {
          description: "Upgrade your plan to deploy marketplace agents.",
        });
        return;
      }
      await deploy.mutateAsync(templateId);
    },
    [deploy, marketplaceEnabled]
  );

  // Save agent settings config
  const handleConfigureAgent = useCallback(
    async (instanceId: string, updates: MarketplaceAgentInstanceConfig) => {
      await updateInstance.mutateAsync({ instanceId, updates: { config: updates } });
    },
    [updateInstance]
  );

  // Sleep/Wake toggle
  const handleToggleAgentStatus = useCallback(
    async (instanceId: string, currentStatus: AgentInstance["status"]) => {
      const nextStatus = currentStatus === "active" ? "paused" : "active";
      await updateInstance.mutateAsync({ instanceId, updates: { status: nextStatus } });
    },
    [updateInstance]
  );

  // Human-in-the-loop actions
  const handleApprovalAction = useCallback(
    async (runId: string, approved: boolean, modifiedOutput?: string) => {
      await processApproval.mutateAsync({ runId, approved, modifiedOutput });
    },
    [processApproval]
  );

  if (!marketplaceEnabled) {
    return (
      <main className="flex-1 p-6 max-w-7xl mx-auto">
        <div className="border rounded-lg p-8 text-center bg-card/25 border-dashed">
          <ShieldCheck className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-bold">Marketplace agents are not available on your plan</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Upgrade to Team or Self-Hosted to deploy AI specialists.
          </p>
        </div>
      </main>
    );
  }

  const isLoading = templatesLoading || instancesLoading || approvalsLoading || logsLoading;

  return (
    <main className="flex-1 p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header section with branding */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            B2B Agent Marketplace
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Provision, monitor, and configure autonomous digital teammates to handle CRM, Sales, and Accounting workflows.
          </p>
        </div>

        <div className="flex items-center gap-1.5 border bg-card/40 backdrop-blur-md p-1.5 rounded-lg text-xs font-semibold text-muted-foreground shadow-sm">
          <ShieldCheck className="h-4 w-4 text-green-500" />
          <span>Principal ID:</span>
          <span className="font-mono text-[10px] bg-muted/60 px-1 py-0.5 rounded text-foreground">
            {user?.id ? `principal_${user.id.substring(0, 6)}` : "mycelium_admin"}
          </span>
        </div>
      </div>

      {isLoading && (
        <div className="text-sm text-muted-foreground">Loading marketplace data...</div>
      )}

      {/* Tabs list with glass aesthetic */}
      <Tabs defaultValue="marketplace" className="space-y-6">
        <TabsList className="bg-card/40 backdrop-blur-md border border-muted/20 p-1 h-auto w-full sm:w-auto flex flex-wrap gap-1">
          <TabsTrigger value="marketplace" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <Bot className="h-4 w-4 shrink-0" />
            Marketplace
          </TabsTrigger>
          <TabsTrigger value="skills" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <BookOpen className="h-4 w-4 shrink-0" />
            Skill Studio
          </TabsTrigger>
          <TabsTrigger value="sandboxes" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <Terminal className="h-4 w-4 shrink-0" />
            Cloud Sandboxes
          </TabsTrigger>
          <TabsTrigger value="swarm" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <Network className="h-4 w-4 shrink-0" />
            Agent Swarm
          </TabsTrigger>
          <TabsTrigger value="coworkers" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <Users className="h-4 w-4 shrink-0" />
            Team Directory
          </TabsTrigger>
          <TabsTrigger value="approvals" className="text-xs gap-1.5 h-8 relative data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <UserCheck className="h-4 w-4 shrink-0" />
            Approval Inbox
            {approvals.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white shadow-sm">
                {approvals.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="telemetry" className="text-xs gap-1.5 h-8 data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
            <BarChart3 className="h-4 w-4 shrink-0" />
            Telemetry
          </TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace">
          <MarketplacePage
            templates={agentTemplates}
            instances={agentInstances}
            onActivateAgent={handleActivateAgent}
            onConfigureAgent={handleOpenConfig}
            onToggleAgentStatus={handleToggleAgentStatus}
            tier={tier}
          />
        </TabsContent>

        <TabsContent value="skills">
          <SkillStudio />
        </TabsContent>

        <TabsContent value="sandboxes">
          <SandboxManager />
        </TabsContent>

        <TabsContent value="swarm">
          <AgentSwarmRoom />
        </TabsContent>

        <TabsContent value="coworkers">
          <TeamDirectory
            members={teamMembers}
            templates={agentTemplates}
            instances={agentInstances}
            onToggleAgentStatus={handleToggleAgentStatus}
            onConfigureAgent={handleOpenConfig}
          />
        </TabsContent>

        <TabsContent value="approvals">
          <ApprovalInbox
            pendingList={approvals}
            onAction={handleApprovalAction}
          />
        </TabsContent>

        <TabsContent value="telemetry">
          <AgentDashboard logs={logs} />
        </TabsContent>
      </Tabs>

      {/* Config Modal */}
      <AgentConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedInstance(null);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        instance={selectedInstance}
        onSave={handleConfigureAgent}
      />
    </main>
  );
}
